import {folderPin, isPinned, Pin, sharePin} from '../../pins'
import type {ShareView} from '../../sharing'
import type {StorageRoot} from '../core/storage'

/**
 * Which rows a pin sorts first. A pin names one folder, so it can only match a row of the listing
 * that directly contains that folder: pinning `A/B/` lists `B` first inside `A/`, and nowhere else.
 */

const NONE: ReadonlySet<string> = new Set()

/**
 * The ids of the folders pinned in a root (paths relative to it, ending in `/`), to list first.
 *
 * - My Files: folder pins; and a share pin for one of the user's own shared folders, which lives
 *   here (the desktop may have pinned it that way).
 * - A shared folder: its share pins with a path.
 */
export function pinnedFolderIds(
  pins: readonly Pin[],
  root: StorageRoot,
  sharedByMe: readonly ShareView[]
): ReadonlySet<string> {
  const ids = new Set<string>()
  for (const pin of pins) {
    if (root.kind === 'own') {
      if (pin.kind === 'folder') {
        ids.add(pin.path)
      } else if (!pin.path) {
        const own = sharedByMe.find((share) => share.shareId === pin.shareId)
        if (own) ids.add(own.folderPath)
      }
    } else if (pin.kind === 'share' && pin.shareId === root.id && pin.path) {
      ids.add(pin.path)
    }
  }
  return ids.size > 0 ? ids : NONE
}

/**
 * The shared folders listed first in the Shared list: share pins without a path, and the user's own
 * shared folders pinned as the folder they are in My Files.
 */
export function pinnedShareIds(
  pins: readonly Pin[],
  shares: readonly ShareView[]
): ReadonlySet<string> {
  const ids = new Set<string>()
  for (const share of shares) {
    if (sharePinCandidates(share).some((candidate) => isPinned(pins, candidate))) {
      ids.add(share.shareId)
    }
  }
  return ids.size > 0 ? ids : NONE
}

/**
 * The pins that stand for a folder of My Files: its path, and also its share when the user shares it
 * (the desktop may have pinned it that way). Pin to top adds the first; Unpin removes them all.
 */
export const folderPinCandidates = (path: string, ownShare?: ShareView): Pin[] =>
  ownShare ? [folderPin(path), sharePin(ownShare.shareId)] : [folderPin(path)]

/**
 * The pins that stand for a shared folder in the Shared list. The user's own shared folder opens
 * where it lives, in My Files, so it is pinned as that folder; a folder someone shares with the user
 * is pinned by share, so the pin keeps working when its owner moves or renames it.
 */
export const sharePinCandidates = (share: ShareView): Pin[] =>
  share.access === 'owner'
    ? [folderPin(share.folderPath), sharePin(share.shareId)]
    : [sharePin(share.shareId)]

/** The pin for a folder inside a shared folder: `path` is relative to the shared folder. */
export const insideSharePinCandidates = (shareId: string, path: string): Pin[] => [
  sharePin(shareId, path),
]
